import React, { useEffect, useRef, useState } from 'react';
import { motion, useSpring, useTransform } from 'motion/react';
import { cn } from '../../lib/utils';
import { useFinePointer } from '../../hooks/useMediaQuery';

/*
 * Soft brand-tinted glow that trails the pointer inside its parent.
 * Adapted from Motion Primitives "Spotlight" (MIT) via 21st.dev:
 * https://21st.dev/ibelick/spotlight (upstream version, listeners cleaned up with an
 * AbortController). The parent must be `relative overflow-hidden`.
 */
const Spotlight = ({ size = 420, className }) => {
  const fine = useFinePointer();
  const ref = useRef(null);
  const [hovered, setHovered] = useState(false);
  const mouseX = useSpring(0, { bounce: 0 });
  const mouseY = useSpring(0, { bounce: 0 });
  const left = useTransform(mouseX, x => `${x - size / 2}px`);
  const top = useTransform(mouseY, y => `${y - size / 2}px`);

  useEffect(() => {
    const parent = ref.current?.parentElement;
    if (!parent || !fine) return undefined;
    const controller = new AbortController();
    const opts = { signal: controller.signal };
    parent.addEventListener(
      'pointermove',
      e => {
        const rect = parent.getBoundingClientRect();
        mouseX.set(e.clientX - rect.left);
        mouseY.set(e.clientY - rect.top);
      },
      opts
    );
    parent.addEventListener('pointerenter', () => setHovered(true), opts);
    parent.addEventListener('pointerleave', () => setHovered(false), opts);
    return () => controller.abort();
  }, [fine, mouseX, mouseY]);

  if (!fine) return null;

  return (
    <motion.div
      ref={ref}
      aria-hidden="true"
      className={cn(
        'pointer-events-none absolute rounded-full blur-2xl transition-opacity duration-300',
        'bg-[radial-gradient(circle_at_center,hsl(var(--brand)/0.22),hsl(var(--brand-2)/0.08)_45%,transparent_70%)]',
        hovered ? 'opacity-100' : 'opacity-0',
        className
      )}
      style={{ width: size, height: size, left, top }}
    />
  );
};

export default Spotlight;
